/**
 * Lab audio (session 302).
 *
 * Tiny Web Audio synthesis layer that produces lab-equipment
 * sounds without any asset files — the chemistry domain shouldn't
 * ship a 5 MB audio bundle just for atmospherics. All sounds are
 * generated on the fly from oscillators + filtered noise:
 *
 *   - **Bunsen burner hiss**: pink noise through a band-pass at
 *     400 Hz + low-pass tail = the soft roar of a propane flame.
 *   - **Pour trickle**: filtered noise modulated by a slow LFO,
 *     volume scaled to the source's flow rate.
 *   - **Drop tick**: short sine ping ~ 1.6 kHz with quick decay,
 *     played each time a droplet lands.
 *   - **Bubble pop**: very short sine + click at 200–600 Hz, low
 *     volume, played when a bubble particle spawns.
 *
 * The AudioContext is lazy — initialised on the first user gesture
 * (browsers block audio creation before interaction). All gain
 * nodes share a master gain so a single setMasterVolume() call
 * silences everything (the user will get a mute toggle in a
 * follow-up session).
 */
let ctx = null;
let master = null;
let burnerNodes = null;
let pourNodes = null;
let gasLeakNodes = null;
// Sustained-loop nodes for sodium sizzle and the stir-rod motor.
// Hoisted up here so `killAllAudio()` can tear them down regardless
// of where in the module they get instantiated. (They're also
// re-declared as the same identifier later for compatibility with
// existing call sites — those declarations are now plain `let`-noops
// since the binding is already established.)
let sizzleNodes = null;
let stirNodes = null;
let masterMuted = false;
/** Cap how loud the lab gets so it doesn't overwhelm a smartboard.
 *  0.30 sits well below the -6 dB ceiling (≈ 0.50 linear) the safety
 *  brief requires for one-shot effects. */
const DEFAULT_MASTER_VOLUME = 0.30;
let MASTER_VOLUME = DEFAULT_MASTER_VOLUME;
/** Session 339a — concurrency cap for one-shot effects. Each one-shot
 *  registers its expected duration; the registry self-prunes on timer
 *  expiry. New requests above the cap are dropped silently. Sustained
 *  loops (burner / pour / sizzle / stir) bypass the cap because they
 *  use a single long-lived source each. */
const ONE_SHOT_CAP = 3;
let activeOneShots = 0;
function tryAcquireOneShot(durationS) {
    if (masterMuted)
        return false;
    if (activeOneShots >= ONE_SHOT_CAP)
        return false;
    activeOneShots++;
    setTimeout(() => { activeOneShots = Math.max(0, activeOneShots - 1); }, Math.ceil(durationS * 1000));
    return true;
}
function ensureContext() {
    if (ctx)
        return ctx;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx)
        return null;
    try {
        ctx = new Ctx();
        master = ctx.createGain();
        master.gain.value = MASTER_VOLUME;
        master.connect(ctx.destination);
        return ctx;
    }
    catch {
        return null;
    }
}
/** Create a noise buffer (white noise, 1 s). Reusable for hiss + pour. */
function makeNoiseBuffer(c) {
    const bufferSize = c.sampleRate;
    const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
    }
    return buffer;
}
/** Resume the audio context if suspended. Browsers auto-suspend
 *  when a tab loses focus or before the first user gesture. */
export function unlockAudio() {
    const c = ensureContext();
    if (c && c.state === "suspended") {
        c.resume().catch(() => { });
    }
}
/** Suspend the audio context. Used on XR sessionend so an active
 *  AudioContext doesn't keep the page "live" enough to block the
 *  Quest browser from closing. Idempotent. */
export function suspendAudio() {
    if (ctx && ctx.state === "running") {
        ctx.suspend().catch(() => { });
    }
}
/**
 * Session 346+ — full kill. Stops EVERY sustained loop, disconnects
 * the master gain, and closes the AudioContext entirely. Called when
 * the lab unmounts (back to lessons / home) so the user never hears
 * residual hiss after leaving the scenario. Unlike `suspendAudio()`
 * which only pauses the context, this tears the graph down so even a
 * later `unlockAudio()` gets a fresh, silent context.
 *
 * After this returns, the next call to `ensureContext()` will spin up
 * a brand-new AudioContext + master gain — no leftover loops can
 * survive the boundary.
 */
export function killAllAudio() {
    // Tear down every known loop's nodes synchronously. We don't try to
    // ramp gain to zero here — the caller wants silence immediately.
    const tear = (nodes) => {
        if (!nodes)
            return;
        try {
            nodes.source.stop();
        }
        catch { /* already stopped */ }
        try {
            nodes.source.disconnect();
        }
        catch { /* */ }
        try {
            nodes.gain.disconnect();
        }
        catch { /* */ }
        if (nodes.lfo) {
            try {
                nodes.lfo.stop();
            }
            catch { /* */ }
            try {
                nodes.lfo.disconnect();
            }
            catch { /* */ }
        }
    };
    tear(burnerNodes);
    tear(pourNodes);
    tear(gasLeakNodes);
    tear(sizzleNodes);
    tear(stirNodes);
    burnerNodes = null;
    pourNodes = null;
    gasLeakNodes = null;
    sizzleNodes = null;
    stirNodes = null;
    if (master) {
        try {
            master.disconnect();
        }
        catch { /* */ }
        master = null;
    }
    if (ctx) {
        try {
            ctx.close().catch(() => undefined);
        }
        catch { /* */ }
        ctx = null;
    }
    activeOneShots = 0;
}
export function setAudioMuted(muted) {
    masterMuted = muted;
    if (master)
        master.gain.value = muted ? 0 : MASTER_VOLUME;
}
export function isAudioMuted() {
    return masterMuted;
}
/** Read the current master volume (0..1, scaled against the
 *  internal cap). Useful for a future settings panel. */
export function getAudioVolume() {
    return MASTER_VOLUME / DEFAULT_MASTER_VOLUME;
}
/** Set the master volume as a 0..1 fraction of the internal cap.
 *  Clamped. Respects the muted state — calling while muted updates
 *  the stored level but keeps output at zero until unmute. */
export function setAudioVolume(value) {
    const clamped = Math.max(0, Math.min(1, value));
    MASTER_VOLUME = clamped * DEFAULT_MASTER_VOLUME;
    if (master && !masterMuted)
        master.gain.value = MASTER_VOLUME;
}
/** Start the Bunsen burner hiss loop. Idempotent; calling while
 *  already running is a no-op. Stops on `stopBurnerHiss()`. */
export function startBurnerHiss() {
    const c = ensureContext();
    if (!c || !master || burnerNodes)
        return;
    const noise = c.createBufferSource();
    noise.buffer = makeNoiseBuffer(c);
    noise.loop = true;
    // Band-pass at ~400 Hz isolates the «roar» band of a flame.
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 380;
    bp.Q.value = 0.6;
    // Low-pass tail to soften the high-end hiss.
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1800;
    const gain = c.createGain();
    gain.gain.value = 0;
    noise.connect(bp).connect(lp).connect(gain).connect(master);
    noise.start();
    // Fade in over 250 ms so it doesn't pop.
    const t0 = c.currentTime;
    gain.gain.linearRampToValueAtTime(0.55, t0 + 0.25);
    burnerNodes = { source: noise, gain };
}
export function stopBurnerHiss() {
    if (!ctx || !burnerNodes)
        return;
    const { source, gain } = burnerNodes;
    burnerNodes = null;
    const t0 = ctx.currentTime;
    gain.gain.cancelScheduledValues(t0);
    gain.gain.setValueAtTime(gain.gain.value, t0);
    gain.gain.linearRampToValueAtTime(0, t0 + 0.20);
    setTimeout(() => {
        try {
            source.stop();
            source.disconnect();
            gain.disconnect();
        }
        catch { /* swept */ }
    }, 250);
}
/** Update the pour-trickle loop. `flowRate` is interpreted in
 *  the same arbitrary units PourSystem uses (m³/s); volume
 *  scales linearly with rate, capped. Pass 0 to silence. */
export function setPourTrickle(flowRate) {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (flowRate <= 1e-7) {
        if (pourNodes) {
            const t0 = c.currentTime;
            pourNodes.gain.gain.cancelScheduledValues(t0);
            pourNodes.gain.gain.linearRampToValueAtTime(0, t0 + 0.10);
            const old = pourNodes;
            pourNodes = null;
            setTimeout(() => {
                try {
                    old.source.stop();
                    old.lfo.stop();
                    old.source.disconnect();
                    old.lfo.disconnect();
                    old.gain.disconnect();
                }
                catch { /* swept */ }
            }, 150);
        }
        return;
    }
    // Scale flowRate (typical 1e-5 .. 2e-4 m³/s) to gain 0.05 .. 0.40.
    const targetGain = Math.min(0.40, 0.05 + flowRate * 1750);
    if (!pourNodes) {
        const noise = c.createBufferSource();
        noise.buffer = makeNoiseBuffer(c);
        noise.loop = true;
        const bp = c.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = 1100;
        bp.Q.value = 1.5;
        const gain = c.createGain();
        gain.gain.value = 0;
        // Slow LFO modulates the gain for a «trickle» rather than a flat hiss.
        const lfo = c.createOscillator();
        const lfoGain = c.createGain();
        lfo.frequency.value = 7;
        lfoGain.gain.value = 0.12;
        lfo.connect(lfoGain).connect(gain.gain);
        noise.connect(bp).connect(gain).connect(master);
        noise.start();
        lfo.start();
        pourNodes = { source: noise, gain, lfo };
        const t0 = c.currentTime;
        gain.gain.linearRampToValueAtTime(targetGain, t0 + 0.10);
    }
    else {
        const t0 = c.currentTime;
        pourNodes.gain.gain.cancelScheduledValues(t0);
        pourNodes.gain.gain.linearRampToValueAtTime(targetGain, t0 + 0.10);
    }
}
/** Gas-leak hiss (session 335i). White-noise band-passed at ~3 kHz
 *  to read as «pressurised gas escaping through a small orifice»,
 *  distinct from the lower-pitched burner roar (380 Hz). Volume
 *  ramps with the number of leaking cylinders so two leaking
 *  sources sound louder than one. Pass 0 to silence. */
export function setGasLeakHiss(intensity) {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (intensity <= 1e-3) {
        if (gasLeakNodes) {
            const t0 = c.currentTime;
            gasLeakNodes.gain.gain.cancelScheduledValues(t0);
            gasLeakNodes.gain.gain.linearRampToValueAtTime(0, t0 + 0.10);
            const old = gasLeakNodes;
            gasLeakNodes = null;
            setTimeout(() => {
                try {
                    old.source.stop();
                    old.source.disconnect();
                    old.gain.disconnect();
                }
                catch { /* swept */ }
            }, 150);
        }
        return;
    }
    const targetGain = Math.min(0.50, 0.10 + intensity * 0.20);
    if (!gasLeakNodes) {
        const noise = c.createBufferSource();
        noise.buffer = makeNoiseBuffer(c);
        noise.loop = true;
        // High band-pass — gas-leak hiss sits much higher than burner.
        const bp = c.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = 3000;
        bp.Q.value = 0.9;
        const gain = c.createGain();
        gain.gain.value = 0;
        noise.connect(bp).connect(gain).connect(master);
        noise.start();
        gasLeakNodes = { source: noise, gain };
        const t0 = c.currentTime;
        gain.gain.linearRampToValueAtTime(targetGain, t0 + 0.10);
    }
    else {
        const t0 = c.currentTime;
        gasLeakNodes.gain.gain.cancelScheduledValues(t0);
        gasLeakNodes.gain.gain.linearRampToValueAtTime(targetGain, t0 + 0.10);
    }
}
/** Single drop tick — short sine ping with quick decay. */
export function playDropTick() {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.12))
        return;
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = 1500 + Math.random() * 600;
    const g = c.createGain();
    const t0 = c.currentTime;
    g.gain.setValueAtTime(0.18, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.10);
    o.connect(g).connect(master);
    o.start(t0);
    o.stop(t0 + 0.12);
}
/** Session 337d — short two-tone warning beep (descending fifth)
 *  played alongside any safety toast. Distinct enough from drop /
 *  bubble / explosion that the ear classifies it instantly as
 *  «something needs attention». ~0.35 s total. */
export function playWarningBeep() {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.36))
        return;
    const out = master;
    const t0 = c.currentTime;
    const tone = (freq, start, dur, vol) => {
        const o = c.createOscillator();
        o.type = "square";
        o.frequency.setValueAtTime(freq, t0 + start);
        const g = c.createGain();
        g.gain.setValueAtTime(vol, t0 + start);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + start + dur);
        o.connect(g).connect(out);
        o.start(t0 + start);
        o.stop(t0 + start + dur + 0.02);
    };
    tone(880, 0, 0.15, 0.18);
    tone(587, 0.18, 0.18, 0.18);
}
/** Bubble pop — very short low sine + tap. Volume kept tiny so a
 *  vigorous fizz doesn't drown everything else. */
export function playBubblePop() {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.06))
        return;
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = 200 + Math.random() * 400;
    const g = c.createGain();
    const t0 = c.currentTime;
    g.gain.setValueAtTime(0.06, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.05);
    o.connect(g).connect(master);
    o.start(t0);
    o.stop(t0 + 0.06);
}
/** Explosion boom (session 305, expanded 308) — low rumble + crack
 *  + bright snap + glass shatter. Four layers of synthesis for a
 *  violent reaction onset (Na+H₂O, thermal decomposition, etc.).
 *  ~0.7 s total. */
export function playExplosionBoom() {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.70))
        return;
    const t0 = c.currentTime;
    // Layer 1 — low-frequency sweep 110 → 35 Hz (rumble).
    const lowOsc = c.createOscillator();
    lowOsc.type = "sine";
    lowOsc.frequency.setValueAtTime(110, t0);
    lowOsc.frequency.exponentialRampToValueAtTime(35, t0 + 0.55);
    const lowGain = c.createGain();
    lowGain.gain.setValueAtTime(0.001, t0);
    lowGain.gain.linearRampToValueAtTime(0.65, t0 + 0.02);
    lowGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.55);
    lowOsc.connect(lowGain).connect(master);
    lowOsc.start(t0);
    lowOsc.stop(t0 + 0.6);
    // Layer 2 — bandpass-filtered noise crack.
    const noise = c.createBufferSource();
    noise.buffer = makeNoiseBuffer(c);
    const noiseBP = c.createBiquadFilter();
    noiseBP.type = "bandpass";
    noiseBP.frequency.value = 800;
    noiseBP.Q.value = 0.6;
    const noiseGain = c.createGain();
    noiseGain.gain.setValueAtTime(0.45, t0);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
    noise.connect(noiseBP).connect(noiseGain).connect(master);
    noise.start(t0);
    noise.stop(t0 + 0.20);
    // Layer 3 — bright high snap.
    const hiOsc = c.createOscillator();
    hiOsc.type = "square";
    hiOsc.frequency.setValueAtTime(1200, t0);
    hiOsc.frequency.exponentialRampToValueAtTime(280, t0 + 0.08);
    const hiGain = c.createGain();
    hiGain.gain.setValueAtTime(0.20, t0);
    hiGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.10);
    hiOsc.connect(hiGain).connect(master);
    hiOsc.start(t0);
    hiOsc.stop(t0 + 0.12);
    // Layer 4 — glass shatter (added 308). High-band noise burst
    // followed by a 4-tone metallic ringing tail (cents apart so the
    // ear hears "broken glass" rather than a chord).
    const shatter = c.createBufferSource();
    shatter.buffer = makeNoiseBuffer(c);
    const shatterHP = c.createBiquadFilter();
    shatterHP.type = "highpass";
    shatterHP.frequency.value = 3500;
    const shatterGain = c.createGain();
    shatterGain.gain.setValueAtTime(0.30, t0 + 0.02);
    shatterGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
    shatter.connect(shatterHP).connect(shatterGain).connect(master);
    shatter.start(t0 + 0.02);
    shatter.stop(t0 + 0.40);
    const ringFreqs = [4200, 5800, 7100, 9300];
    for (const f of ringFreqs) {
        const o = c.createOscillator();
        o.type = "sine";
        o.frequency.value = f * (0.96 + Math.random() * 0.08);
        const g = c.createGain();
        g.gain.setValueAtTime(0.04, t0 + 0.04);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.45);
        o.connect(g).connect(master);
        o.start(t0 + 0.04);
        o.stop(t0 + 0.50);
    }
}
// `sizzleNodes` declared at module top.
/** Start the sodium-water sizzle — rough crackling sound that
 *  layers under the boom for the duration of the violent reaction.
 *  Real sodium sounds like a continuous angry hiss + crackle while
 *  hydrogen burns on the surface. (Session 318c.) */
export function startSodiumSizzle() {
    const c = ensureContext();
    if (!c || !master || sizzleNodes)
        return;
    const noise = c.createBufferSource();
    noise.buffer = makeNoiseBuffer(c);
    noise.loop = true;
    // High band-pass for the «crackle» feel — rough white-ish hiss.
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1800;
    bp.Q.value = 1.4;
    // Slight LFO on the gain for a flickering / unstable feel.
    const lfo = c.createOscillator();
    lfo.frequency.value = 11;
    const lfoGain = c.createGain();
    lfoGain.gain.value = 0.20;
    const gain = c.createGain();
    gain.gain.value = 0;
    lfo.connect(lfoGain).connect(gain.gain);
    noise.connect(bp).connect(gain).connect(master);
    noise.start();
    lfo.start();
    const t0 = c.currentTime;
    gain.gain.linearRampToValueAtTime(0.18, t0 + 0.10);
    sizzleNodes = { source: noise, gain, lfo };
}
export function stopSodiumSizzle() {
    if (!ctx || !sizzleNodes)
        return;
    const { source, gain, lfo } = sizzleNodes;
    sizzleNodes = null;
    const t0 = ctx.currentTime;
    gain.gain.cancelScheduledValues(t0);
    gain.gain.setValueAtTime(gain.gain.value, t0);
    gain.gain.linearRampToValueAtTime(0, t0 + 0.30);
    setTimeout(() => {
        try {
            source.stop();
            lfo.stop();
            source.disconnect();
            lfo.disconnect();
            gain.disconnect();
        }
        catch { /* swept */ }
    }, 350);
}
/** Place thud — short low resonance for putting a vessel down on
 *  the bench (session 308). Intensity ∈ [0.3, 1.0] scales volume +
 *  punch (drop height proxy: 0.0 m → soft tap, 0.20+ m → hard drop). */
export function playPlaceThud(intensity) {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.20))
        return;
    const k = Math.max(0.3, Math.min(1.0, intensity));
    const t0 = c.currentTime;
    // Body — low sine drop 140 → 70 Hz.
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(140, t0);
    o.frequency.exponentialRampToValueAtTime(70, t0 + 0.10);
    const g = c.createGain();
    g.gain.setValueAtTime(0.001, t0);
    g.gain.linearRampToValueAtTime(0.30 * k, t0 + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
    o.connect(g).connect(master);
    o.start(t0);
    o.stop(t0 + 0.20);
    // Click — band-pass noise on the impact onset.
    const n = c.createBufferSource();
    n.buffer = makeNoiseBuffer(c);
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1200;
    bp.Q.value = 1.5;
    const ng = c.createGain();
    ng.gain.setValueAtTime(0.18 * k, t0);
    ng.gain.exponentialRampToValueAtTime(0.001, t0 + 0.06);
    n.connect(bp).connect(ng).connect(master);
    n.start(t0);
    n.stop(t0 + 0.08);
}
/** Glass-on-glass clink — bright sine ping with quick decay
 *  (session 308). Two close-tuned partials so it sounds like
 *  glass rather than a bell. */
export function playGlassClink() {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.20))
        return;
    const t0 = c.currentTime;
    const partials = [
        [3200 + Math.random() * 400, 0.22],
        [4700 + Math.random() * 600, 0.14],
    ];
    for (const [f, vol] of partials) {
        const o = c.createOscillator();
        o.type = "sine";
        o.frequency.value = f;
        const g = c.createGain();
        g.gain.setValueAtTime(vol, t0);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
        o.connect(g).connect(master);
        o.start(t0);
        o.stop(t0 + 0.20);
    }
    // Tiny noise transient on the strike.
    const n = c.createBufferSource();
    n.buffer = makeNoiseBuffer(c);
    const hp = c.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 4000;
    const ng = c.createGain();
    ng.gain.setValueAtTime(0.10, t0);
    ng.gain.exponentialRampToValueAtTime(0.001, t0 + 0.04);
    n.connect(hp).connect(ng).connect(master);
    n.start(t0);
    n.stop(t0 + 0.05);
}
/** Drawer slide whoosh (session 308). `opening` controls duration
 *  and pitch — opening sweeps low → high, closing high → low, with
 *  a soft thump at the end. */
export function playDrawerSlide(opening) {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.40))
        return;
    const t0 = c.currentTime;
    const dur = 0.28;
    // Wooden slide noise — low-pass-filtered noise with a slow gain
    // envelope.
    const n = c.createBufferSource();
    n.buffer = makeNoiseBuffer(c);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(opening ? 600 : 900, t0);
    lp.frequency.exponentialRampToValueAtTime(opening ? 1100 : 500, t0 + dur);
    const ng = c.createGain();
    ng.gain.setValueAtTime(0.001, t0);
    ng.gain.linearRampToValueAtTime(0.15, t0 + 0.05);
    ng.gain.linearRampToValueAtTime(0.10, t0 + dur - 0.04);
    ng.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    n.connect(lp).connect(ng).connect(master);
    n.start(t0);
    n.stop(t0 + dur + 0.05);
    // Soft thump at arrival.
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = 90;
    const g = c.createGain();
    g.gain.setValueAtTime(0.001, t0 + dur - 0.02);
    g.gain.linearRampToValueAtTime(0.18, t0 + dur);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur + 0.10);
    o.connect(g).connect(master);
    o.start(t0 + dur - 0.02);
    o.stop(t0 + dur + 0.12);
}
/** Faucet metallic click (session 308). Two partials + tiny noise
 *  for the «tap turning» feel. */
export function playFaucetClick() {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.07))
        return;
    const t0 = c.currentTime;
    const o1 = c.createOscillator();
    o1.type = "square";
    o1.frequency.value = 1800;
    const g1 = c.createGain();
    g1.gain.setValueAtTime(0.10, t0);
    g1.gain.exponentialRampToValueAtTime(0.001, t0 + 0.04);
    o1.connect(g1).connect(master);
    o1.start(t0);
    o1.stop(t0 + 0.05);
    const o2 = c.createOscillator();
    o2.type = "sine";
    o2.frequency.value = 2400;
    const g2 = c.createGain();
    g2.gain.setValueAtTime(0.08, t0);
    g2.gain.exponentialRampToValueAtTime(0.001, t0 + 0.06);
    o2.connect(g2).connect(master);
    o2.start(t0);
    o2.stop(t0 + 0.07);
}
/** Balance tare confirmation — short two-tone electronic beep
 *  (700 Hz → 1100 Hz, ~0.10 s) for «vessel registered on the
 *  balance». Square waves keep it readable as a digital chirp. */
export function playBalanceTare() {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.12))
        return;
    const out = master;
    const t0 = c.currentTime;
    const tone = (freq, start, dur) => {
        const o = c.createOscillator();
        o.type = "square";
        o.frequency.setValueAtTime(freq, t0 + start);
        const g = c.createGain();
        g.gain.setValueAtTime(0.001, t0 + start);
        g.gain.linearRampToValueAtTime(0.14, t0 + start + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + start + dur);
        o.connect(g).connect(out);
        o.start(t0 + start);
        o.stop(t0 + start + dur + 0.02);
    };
    tone(700, 0, 0.05);
    tone(1100, 0.05, 0.05);
}
/** Acid-fizz burst — high-passed white-noise pop scaled by
 *  `intensity` ∈ [0.3, 1.0]. ~0.4 s. For acid+metal H₂ evolution
 *  onset (and any short fizzy reaction punch). */
export function playAcidFizz(intensity = 1.0) {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.42))
        return;
    const k = Math.max(0.3, Math.min(1.0, intensity));
    const t0 = c.currentTime;
    const noise = c.createBufferSource();
    noise.buffer = makeNoiseBuffer(c);
    const hp = c.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 2000;
    const g = c.createGain();
    g.gain.setValueAtTime(0.001, t0);
    g.gain.linearRampToValueAtTime(0.30 * k, t0 + 0.04);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.40);
    noise.connect(hp).connect(g).connect(master);
    noise.start(t0);
    noise.stop(t0 + 0.42);
}
// `stirNodes` declared at module top.
/** Magnetic-stirrer rumble — sustained low (60-90 Hz random LFO)
 *  while the stirrer is on. Pass `false` to fade out. Idempotent. */
export function playStirRing(active) {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!active) {
        if (!stirNodes)
            return;
        const { source, gain, lfo } = stirNodes;
        stirNodes = null;
        const t0 = c.currentTime;
        gain.gain.cancelScheduledValues(t0);
        gain.gain.setValueAtTime(gain.gain.value, t0);
        gain.gain.linearRampToValueAtTime(0, t0 + 0.20);
        setTimeout(() => {
            try {
                source.stop();
                lfo.stop();
                source.disconnect();
                lfo.disconnect();
                gain.disconnect();
            }
            catch { /* swept */ }
        }, 250);
        return;
    }
    if (stirNodes)
        return;
    // Carrier — low-pass filtered noise (the «motor» body).
    const noise = c.createBufferSource();
    noise.buffer = makeNoiseBuffer(c);
    noise.loop = true;
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 120;
    // LFO sweeps the low-pass cutoff between ~60 and ~90 Hz so the
    // rumble breathes rather than sitting flat.
    const lfo = c.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 4.5;
    const lfoGain = c.createGain();
    lfoGain.gain.value = 15;
    lfo.connect(lfoGain).connect(lp.frequency);
    const gain = c.createGain();
    gain.gain.value = 0;
    noise.connect(lp).connect(gain).connect(master);
    noise.start();
    lfo.start();
    const t0 = c.currentTime;
    gain.gain.linearRampToValueAtTime(0.35, t0 + 0.20);
    stirNodes = { source: noise, gain, lfo };
}
/** Glass-break crack — broadband 200 Hz noise burst plus a
 *  high-shelf filter sweep for the shatter tail. ~0.6 s. For
 *  vessel-falls-off-bench / capacity-rejection drop events. */
export function playGlassBreak() {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.62))
        return;
    const t0 = c.currentTime;
    // Body — low band-passed noise burst (the «crack»).
    const body = c.createBufferSource();
    body.buffer = makeNoiseBuffer(c);
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 200;
    bp.Q.value = 0.7;
    const bg = c.createGain();
    bg.gain.setValueAtTime(0.001, t0);
    bg.gain.linearRampToValueAtTime(0.45, t0 + 0.01);
    bg.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
    body.connect(bp).connect(bg).connect(master);
    body.start(t0);
    body.stop(t0 + 0.20);
    // Shatter tail — high-shelf swept noise for the «glass shards».
    const tail = c.createBufferSource();
    tail.buffer = makeNoiseBuffer(c);
    const hs = c.createBiquadFilter();
    hs.type = "highshelf";
    hs.frequency.setValueAtTime(2000, t0);
    hs.frequency.exponentialRampToValueAtTime(8000, t0 + 0.55);
    hs.gain.value = 12;
    const tg = c.createGain();
    tg.gain.setValueAtTime(0.30, t0 + 0.02);
    tg.gain.exponentialRampToValueAtTime(0.001, t0 + 0.60);
    tail.connect(hs).connect(tg).connect(master);
    tail.start(t0 + 0.02);
    tail.stop(t0 + 0.62);
}
/** Pipette suck — quick descending sweep 1500 → 600 Hz with a
 *  small noise tail. ~0.25 s. For drawing liquid into a pipette. */
export function playPipetteSuck() {
    const c = ensureContext();
    if (!c || !master)
        return;
    if (!tryAcquireOneShot(0.26))
        return;
    const t0 = c.currentTime;
    // Pitch sweep — sawtooth so it reads as «air rushing through a tip».
    const o = c.createOscillator();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(1500, t0);
    o.frequency.exponentialRampToValueAtTime(600, t0 + 0.20);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 2200;
    const g = c.createGain();
    g.gain.setValueAtTime(0.001, t0);
    g.gain.linearRampToValueAtTime(0.16, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
    o.connect(lp).connect(g).connect(master);
    o.start(t0);
    o.stop(t0 + 0.24);
    // Noise tail — tiny breath at the end.
    const n = c.createBufferSource();
    n.buffer = makeNoiseBuffer(c);
    const nbp = c.createBiquadFilter();
    nbp.type = "bandpass";
    nbp.frequency.value = 1800;
    nbp.Q.value = 1.2;
    const ng = c.createGain();
    ng.gain.setValueAtTime(0.001, t0 + 0.18);
    ng.gain.linearRampToValueAtTime(0.08, t0 + 0.20);
    ng.gain.exponentialRampToValueAtTime(0.001, t0 + 0.25);
    n.connect(nbp).connect(ng).connect(master);
    n.start(t0 + 0.18);
    n.stop(t0 + 0.26);
}
